import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Formik, getIn, useFormikContext } from 'formik';
import { ArrowDown2 } from 'iconsax-react';
import {
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Yup from 'yup';

import { SlabContext } from '@core/context/SlabContext';
import type { ContactData } from '@core/hooks/useContactCreation';
import { Company } from '@core/types/api/Company/Company';
import { Contact } from '@core/types/api/Contact/Contact';
import Enums from '@core/types/api/Enums';
import type { Market } from '@core/types/api/Market/Market';
import { requiredLabel } from '@core/utils/strings';
import { ExpandMore } from '@web/components/ExpandMore/ExpandMore';
import { Input } from '@web/components/Input/Input';
import {
  InputMultiLookup,
  ReferenceValueMatcher,
} from '@web/components/InputMultiLookup/InputMultiLookup';
import type { LookupInputOption } from '@web/components/LookupInput/LookupInputSharedComponents';
import { SlabCard } from '@web/components/SlabCard/SlabCard';
import { SlabCardBody } from '@web/components/SlabCard/SlabCardBody';
import { SlabCardHeader } from '@web/components/SlabCard/SlabCardHeader';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { ServerErrorMessage, useSlabQuery } from '@web/hooks/useSlabQuery';
import { useToast } from '@web/providers/ToastProvider/useToast';
import { lookups } from '@web/utils/DomainHelpers';

type CompanyReference = {
  id: string;
  option: { value: string; label: string };
};

type Props = {
  children: ReactNode;
  disabled?: boolean;
  onCompanyCreated: (company: CompanyReference, contact: ContactData | null) => void;
  /** When true, renders Create/Select radio buttons instead of a plain button toggle. */
  showRadioButtons?: boolean;
  /** When true, hides the ability to create a new company entirely. */
  disableCreate?: boolean;
};

type FormValues = {
  name: string;
  markets: { id: string; option: { value: string; label: string } }[];
  // Progressive disclosure — contact
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  // Progressive disclosure — address
  addressLine1: string;
  addressCity: string;
  addressState: string;
  addressPostalCode: string;
};

const marketItemSchema = Yup.object({
  id: Yup.string().required(),
  option: Yup.object({
    value: Yup.string().required(),
    label: Yup.string().required(),
  }).required(),
}).required();

export const InlineCompanyCreate: FC<Props> = ({
  children,
  disabled,
  onCompanyCreated,
  showRadioButtons = false,
  disableCreate = false,
}) => {
  const theme = useTheme();
  const ctx = useContext(SlabContext);
  const toast = useToast();

  // Access the outer quote Formik so we can touch / surface the company field error
  // when the inner create form is shown instead of the ApiLookupInput.
  const outerFormik = useFormikContext<any>();

  const [mode, setMode] = useState<'select' | 'create'>('select');
  const [isOpen, setIsOpen] = useState(true);
  const [contactExpanded, setContactExpanded] = useState(false);
  const [addressExpanded, setAddressExpanded] = useState(false);
  const [createFormKey, setCreateFormKey] = useState(0);
  const attemptRef = useRef(0);
  const wasOuterDirtyRef = useRef(outerFormik.dirty);
  const previousCompanyRef = useRef<unknown>(null);

  // Touch the outer company field whenever a submit is attempted while in create mode,
  // so the validation error becomes visible.
  useEffect(() => {
    if (showRadioButtons && mode === 'create' && outerFormik.submitCount > 0) {
      outerFormik.setFieldTouched('company', true, false);
    }
  }, [outerFormik.submitCount]);

  // When the outer form is reset, also reset the inline create-company UI/form.
  useEffect(() => {
    const wasDirty = wasOuterDirtyRef.current;
    const wasReset = wasDirty && !outerFormik.dirty;

    if (wasReset) {
      attemptRef.current += 1;
      previousCompanyRef.current = null;
      setContactExpanded(false);
      setAddressExpanded(false);
      setMode('select');
      setCreateFormKey((prev) => prev + 1);
      outerFormik.setFieldTouched('company', false, false);
      outerFormik.setFieldError('company', undefined);
    }

    wasOuterDirtyRef.current = outerFormik.dirty;
  }, [outerFormik.dirty, outerFormik]);

  const areMarketsRequired = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresCompanyMarkets,
  ]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required('Company name is required'),
        markets: areMarketsRequired
          ? Yup.array().of(marketItemSchema).min(1, 'At least one market is required')
          : Yup.array().of(marketItemSchema),
        contactFirstName: Yup.string(),
        contactLastName: Yup.string(),
        contactEmail: Yup.string().email('Invalid email address'),
        contactPhone: Yup.string(),
        addressLine1: Yup.string(),
        addressCity: Yup.string(),
        addressState: Yup.string(),
        addressPostalCode: Yup.string(),
      }),
    [areMarketsRequired],
  );

  const { data: marketList } = useSlabQuery('GET markets', {});
  const marketOpts: LookupInputOption[] =
    marketList === undefined ? [] : lookups({ ...marketList, label: (m: Market) => m.name ?? '' });

  const createContact = useSlabMutation('POST contact', {
    onError: (error) => {
      toast.error({ message: ServerErrorMessage(error) });
    },
  });

  const createCompany = useSlabMutation('POST company', {
    onError: (error) => {
      toast.error({ message: ServerErrorMessage(error) });
    },
  });

  const handleCompanyCreated = (data: Company, values: FormValues, attempt: number): void => {
    const isStale = (): boolean => attempt !== attemptRef.current;
    if (isStale()) {
      return;
    }

    const company = { id: data.id, option: { value: data.id, label: data.name } };
    const finish = (contact: ContactData | null): void => {
      if (isStale()) {
        return;
      }
      onCompanyCreated(company, contact);
      setMode('select');
    };

    // If contact fields were provided, create the contact linked to the new company
    if (values.contactFirstName.trim() === '' && values.contactLastName.trim() === '') {
      finish(null);
      return;
    }

    const wireContact = new Contact({
      firstName: values.contactFirstName,
      lastName: values.contactLastName,
      emailAddress: values.contactEmail || null,
      phoneNumber: values.contactPhone || null,
      company: { id: data.id },
    });
    createContact.mutate(
      { args: { body: wireContact } },
      {
        onSuccess: (contact) =>
          finish({
            id: contact.id,
            option: { value: contact.id, label: contact.fullName() },
            isNewContact: false,
          }),
        onError: () => finish(null),
      },
    );
  };

  useEffect(() => {
    if (disabled && mode === 'create') {
      setMode('select');
    }
  }, [disabled, mode]);

  const handleCancel = useCallback(() => {
    attemptRef.current += 1;
    setContactExpanded(false);
    setAddressExpanded(false);
    setMode('select');
    if (previousCompanyRef.current !== null) {
      outerFormik.setFieldValue('company', previousCompanyRef.current);
      previousCompanyRef.current = null;
    }
  }, [outerFormik]);

  const handleSubmit = (values: FormValues): void => {
    attemptRef.current += 1;
    const attempt = attemptRef.current;
    const hasAddress =
      values.addressLine1.trim() !== '' ||
      values.addressCity.trim() !== '' ||
      values.addressPostalCode.trim() !== '';
    const wireCompany = new Company({
      name: values.name,
      category: Enums.CompanyCategory.Contractor,
      marketSummaries: values.markets.map((m) => ({ id: m.id })),
      address: hasAddress
        ? {
            line1: values.addressLine1 || null,
            city: values.addressCity || null,
            state: values.addressState,
            postalCode: values.addressPostalCode || null,
          }
        : undefined,
    });
    createCompany.mutate(
      { args: { body: wireCompany } },
      { onSuccess: (data) => handleCompanyCreated(data, values, attempt) },
    );
  };

  const companyCreateForm = (
    <Formik<FormValues>
      key={createFormKey}
      initialValues={{
        name: '',
        markets: [],
        contactFirstName: '',
        contactLastName: '',
        contactEmail: '',
        contactPhone: '',
        addressLine1: '',
        addressCity: '',
        addressState: '',
        addressPostalCode: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikBag) => {
        const hasCompanyName = formikBag.values.name.trim() !== '';
        return (
          <>
            <Input
              name='name'
              label={requiredLabel('Company name', true)}
              onInputChange={(value) => {
                if (!showRadioButtons) {
                  return;
                }

                if (value.trim() !== '') {
                  outerFormik.setFieldTouched('company', false, false);
                  outerFormik.setFieldError('company', undefined);
                  return;
                }

                if (outerFormik.submitCount > 0) {
                  outerFormik.setFieldTouched('company', true, false);
                }
              }}
            />
            {marketOpts.length > 0 && (
              <InputMultiLookup
                name='markets'
                label={requiredLabel('Markets', areMarketsRequired)}
                withCheckbox
                tip='Select specific markets otherwise the company will be associated with all markets.'
                options={marketOpts}
                onMatchChange={(matches): void => {
                  formikBag.setFieldValue(
                    'markets',
                    matches.map(({ value, label }) => ({
                      id: value,
                      option: { value, label },
                    })),
                  );
                }}
                formValueMatcher={ReferenceValueMatcher}
              />
            )}

            {/* Progressive disclosure — only shown once a company name is entered */}
            {hasCompanyName && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                {/* Add contact */}
                <Box>
                  <Button
                    type='button'
                    onClick={() => setContactExpanded((prev) => !prev)}
                    sx={{
                      textTransform: 'none',
                      color: theme.palette.SlabIndigo[500],
                      p: 0,
                      fontWeight: 400,
                    }}
                  >
                    {contactExpanded ? '− Contact (optional)' : '+ Add a contact (optional)'}
                  </Button>
                  <Collapse in={contactExpanded} timeout='auto' unmountOnExit>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1, pl: 1 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Input name='contactFirstName' label='First name' />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Input name='contactLastName' label='Last name' />
                        </Box>
                      </Box>
                      <Input name='contactEmail' label='Email' />
                      <Input name='contactPhone' label='Phone' />
                    </Box>
                  </Collapse>
                </Box>

                {/* Add address */}
                <Box>
                  <Button
                    type='button'
                    onClick={() => setAddressExpanded((prev) => !prev)}
                    sx={{
                      textTransform: 'none',
                      color: theme.palette.SlabIndigo[500],
                      p: 0,
                      fontWeight: 400,
                    }}
                  >
                    {addressExpanded
                      ? '− Company address (optional)'
                      : '+ Add company address (optional)'}
                  </Button>
                  <Collapse in={addressExpanded} timeout='auto' unmountOnExit>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1, pl: 1 }}>
                      <Input name='addressLine1' label='Street address' />
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Input name='addressCity' label='City' />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Input name='addressState' label='State' />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Input name='addressPostalCode' label='ZIP' />
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', pt: 1 }}>
              <Button
                type='button'
                onClick={handleCancel}
                sx={{ textTransform: 'none', color: theme.palette.SlabGray[800] }}
              >
                Cancel
              </Button>
              <Button
                type='button'
                variant='contained'
                onClick={async () => {
                  const errors = await formikBag.validateForm();
                  if (Object.keys(errors).length > 0) {
                    formikBag.setTouched(
                      Object.fromEntries(Object.keys(errors).map((k) => [k, true])),
                      false,
                    );
                    return;
                  }
                  formikBag.submitForm();
                }}
                disabled={formikBag.isSubmitting || createCompany.isPending}
                sx={{
                  backgroundColor: theme.palette.SlabIndigo[500],
                  '&:hover': { backgroundColor: theme.palette.SlabIndigo[700] },
                }}
              >
                Create
              </Button>
            </Box>
          </>
        );
      }}
    </Formik>
  );

  // ── Radio-buttons mode (used in Quick quote) ─────────────────────────────
  if (showRadioButtons && !disableCreate) {
    const outerCompanyError =
      mode === 'create' && getIn(outerFormik.touched, 'company')
        ? (getIn(outerFormik.errors, 'company') as string | undefined)
        : undefined;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <RadioGroup
          row
          value={mode}
          onChange={(_, val) => {
            const newMode = val as 'select' | 'create';
            if (newMode === 'select') {
              attemptRef.current += 1;
            }
            setContactExpanded(false);
            setAddressExpanded(false);
            setMode(newMode);
            if (newMode === 'create') {
              // Touch the company field so errors surface when the user clicks Save
              // without completing the form.
              outerFormik.setFieldTouched('company', true, false);
              // Set a sentinel so the outer form becomes dirty, enabling the Reset button.
              previousCompanyRef.current = outerFormik.values.company;
              outerFormik.setFieldValue('company', { id: '', option: null });
            } else if (previousCompanyRef.current !== null) {
              // Restore the previous company value when switching back to select mode.
              outerFormik.setFieldValue('company', previousCompanyRef.current);
              previousCompanyRef.current = null;
            }
          }}
        >
          <FormControlLabel
            value='select'
            control={<Radio size='small' disabled={disabled} />}
            label='Select existing company'
          />
          <FormControlLabel
            value='create'
            control={<Radio size='small' disabled={disabled} />}
            label='Create new company'
          />
        </RadioGroup>

        {mode === 'select' ? (
          children
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {outerCompanyError !== undefined && typeof outerCompanyError === 'string' && (
              <Typography
                variant='body2'
                sx={{ color: theme.palette.error.main, fontSize: 12, mt: -0.5 }}
              >
                {outerCompanyError}
              </Typography>
            )}
            {companyCreateForm}
          </Box>
        )}
      </Box>
    );
  }

  // ── Original button mode (used in Full quote / Buyer Information) ─────────
  if (mode === 'select') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {children}
        {!disableCreate && (
          <Button
            type='button'
            disabled={disabled}
            onClick={() => {
              previousCompanyRef.current = outerFormik.values.company;
              outerFormik.setFieldValue('company', { id: '', option: null });
              setMode('create');
            }}
            sx={{
              alignSelf: 'flex-start',
              textTransform: 'none',
              color: theme.palette.SlabIndigo[500],
              p: 0,
            }}
          >
            + Create new company
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Formik<FormValues>
      initialValues={{
        name: '',
        markets: [],
        contactFirstName: '',
        contactLastName: '',
        contactEmail: '',
        contactPhone: '',
        addressLine1: '',
        addressCity: '',
        addressState: '',
        addressPostalCode: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikBag) => (
        <SlabCard>
          <SlabCardHeader
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant='h6'>New Company</Typography>
            <ExpandMore expand={isOpen} onClick={() => setIsOpen(!isOpen)}>
              <ArrowDown2 />
            </ExpandMore>
          </SlabCardHeader>

          <Collapse in={isOpen} timeout='auto' unmountOnExit>
            <SlabCardBody>
              <Input name='name' label={requiredLabel('Company name', true)} />
              {marketOpts.length > 0 && (
                <InputMultiLookup
                  name='markets'
                  label={requiredLabel('Markets', areMarketsRequired)}
                  withCheckbox
                  tip='Select specific markets otherwise the company will be associated with all markets.'
                  options={marketOpts}
                  onMatchChange={(matches): void => {
                    formikBag.setFieldValue(
                      'markets',
                      matches.map(({ value, label }) => ({
                        id: value,
                        option: { value, label },
                      })),
                    );
                  }}
                  formValueMatcher={ReferenceValueMatcher}
                />
              )}

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', pt: 1 }}>
                <Button
                  type='button'
                  variant='contained'
                  onClick={async () => {
                    const errors = await formikBag.validateForm();
                    if (Object.keys(errors).length > 0) {
                      formikBag.setTouched(
                        Object.fromEntries(Object.keys(errors).map((k) => [k, true])),
                        false,
                      );
                      return;
                    }
                    formikBag.submitForm();
                  }}
                  disabled={formikBag.isSubmitting || createCompany.isPending}
                  sx={{
                    backgroundColor: theme.palette.SlabIndigo[500],
                    '&:hover': { backgroundColor: theme.palette.SlabIndigo[700] },
                  }}
                >
                  Create
                </Button>
                <Button
                  type='button'
                  onClick={handleCancel}
                  sx={{ textTransform: 'none', color: theme.palette.SlabGray[800] }}
                >
                  Cancel
                </Button>
              </Box>
            </SlabCardBody>
          </Collapse>
        </SlabCard>
      )}
    </Formik>
  );
};
